import { useRef, useState } from 'react';
import { Box, Typography, Button, IconButton, Stack, CircularProgress, Tooltip, TextField } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import { useTranslation } from 'react-i18next';
import {
  useScopeAttachments, useUploadScopeAttachment, useDeleteScopeAttachment,
  useUpdateScopeAttachmentDescription, downloadScopeAttachment, isImageAttachment,
  attachmentMarkdown, SCOPE_DESCRIPTION_MAX, type ScopeAttachment,
} from '../../api/hooks/useScopeAttachments';
import { ScopeGallery } from './ScopeGallery';
import { useNotifications } from '../../context/NotificationContext';
import { formatApiError } from '../../api/client';

function formatSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DescriptionField({ att, onSave }: { att: ScopeAttachment; onSave: (description: string) => Promise<void> }) {
  const { t } = useTranslation('projects');
  const [text, setText] = useState(att.description ?? '');
  return (
    <TextField
      size="small" variant="standard" fullWidth
      value={text}
      placeholder={t('scope.attachments.descriptionPlaceholder', 'Kurze Beschreibung, z. B. „Angebot von Lieferant vom 21.02.2026“')}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => { if (text !== (att.description ?? '')) void onSave(text); }}
      slotProps={{ htmlInput: { 'aria-label': t('scope.attachments.description', 'Beschreibung'), maxLength: SCOPE_DESCRIPTION_MAX } }}
    />
  );
}

/**
 * Bilder und Dateien zum Scope Statement: Upload, Liste mit Kurzbeschreibung je
 * Datei, Download, Löschen. Bilder lassen sich per Knopf als Markdown-Verweis in
 * den Text einfügen (`onInsert`).
 */
export function ScopeAttachments({ projectId, onInsert }: { projectId: string; onInsert: (markdown: string) => void }) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useScopeAttachments(projectId);
  const items = ((data as unknown as { data?: ScopeAttachment[] } | undefined)?.data ?? []);
  const uploadMut = useUploadScopeAttachment(projectId);
  const deleteMut = useDeleteScopeAttachment(projectId);
  const descMut = useUpdateScopeAttachmentDescription(projectId);

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) await uploadMut.mutateAsync(f);
      notify(t('attachments.uploaded', 'Datei angehängt'), 'success');
    } catch (err) {
      notify(formatApiError(err), 'error');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const run = async (fn: () => Promise<unknown>) => {
    try { await fn(); } catch (err) { notify(formatApiError(err), 'error'); }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ color: 'var(--pm-fg-muted)', fontWeight: 600 }}>
          {t('scope.attachments.title', 'Bilder und Dateien')}{items.length > 0 ? ` (${items.length})` : ''}
        </Typography>
        <Button
          size="small"
          startIcon={busy ? <CircularProgress size={14} /> : <AttachFileIcon fontSize="small" />}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          sx={{ color: 'var(--pm-fg-muted)' }}
        >
          {t('attachments.add', 'Datei anhängen')}
        </Button>
        <input
          ref={inputRef} type="file" multiple hidden data-testid="scope-attachment-input"
          onChange={(e) => void onFiles(e.target.files)}
        />
      </Stack>

      {isLoading ? (
        <CircularProgress size={18} />
      ) : items.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
          {t('attachments.empty', 'Noch keine Anhänge.')}
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {items.map((att) => (
            <Stack key={att.id} direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: 'var(--pm-bg-subtle)' }}>
              {isImageAttachment(att)
                ? <ImageIcon fontSize="small" sx={{ color: 'var(--pm-fg-subtle)' }} />
                : <InsertDriveFileIcon fontSize="small" sx={{ color: 'var(--pm-fg-subtle)' }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: 'var(--pm-fg-base)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {att.filename}
                </Typography>
                <DescriptionField att={att} onSave={(description) => run(() => descMut.mutateAsync({ id: att.id, description }))} />
              </Box>
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>{formatSize(att.size)}</Typography>
              {isImageAttachment(att) && (
                <Button size="small" onClick={() => onInsert(attachmentMarkdown(att))}>
                  {t('scope.attachments.insert', 'In Text einfügen')}
                </Button>
              )}
              <Tooltip title={tc('actions.download', 'Herunterladen')} arrow>
                <IconButton size="small" aria-label={tc('actions.download', 'Herunterladen')} onClick={() => void run(() => downloadScopeAttachment(projectId, att))} sx={{ color: 'var(--pm-fg-muted)' }}><DownloadIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title={tc('actions.delete', 'Löschen')} arrow>
                <IconButton size="small" aria-label={tc('actions.delete', 'Löschen')} onClick={() => void run(async () => { await deleteMut.mutateAsync(att.id); notify(tc('status.deleted', 'Gelöscht'), 'success'); })} sx={{ color: 'var(--pm-error)' }}><DeleteIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      )}

      {!isLoading && <ScopeGallery projectId={projectId} attachments={items} />}
    </Box>
  );
}
